import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal, TriangleAlert } from "lucide-react";
import Loader from "../common/Loader";

const CheckboxIcon = ({ checked }) => (
  <span
    className={[
      "inline-flex h-[14px] text-xs w-[14px] items-center justify-center  border-[0.5px]",
      checked
        ? "border-[#A04BA0] bg-[#A04BA0] text-white"
        : "border-[#923D8C] bg-[#F2F2F2] text-transparent",
    ].join(" ")}
  >
    ✓
  </span>
);

const UserIdentity = ({ row }) => {
  const displayName =
    [row.firstName, row.lastName].filter(Boolean).join(" ") || row.name || "-";
  const avatarUrl =
    row.profilePicture?.secureUrl || row.avatarUrl || row.photoUrl;
  const initials =
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?";

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#E8DCEA] text-sm font-semibold text-[#923D8C]">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={displayName}
            className="h-full w-full object-cover"
          />
        ) : (
          initials
        )}
      </div>

      <span className="whitespace-nowrap text-sm font-medium text-[#111827]">
        {displayName}
      </span>
    </div>
  );
};

const StatusBadge = ({ status, note, statusStyleMap }) => {
  const statusClass = statusStyleMap[status.toLowerCase()];
  return (
    <div className="inline-flex flex-col items-center">
      <span
        className={[
          "inline-flex justify-center capitalize rounded-full border w-20  py-1 text-sm leading-4",
          statusClass,
        ].join(" ")}
      >
        {status}
      </span>
      {note ? (
        <span className="inline-flex items-center gap-0.5 text-xs  text-[#DC2626]">
          <TriangleAlert className="h-3 w-3" />
          {note}
        </span>
      ) : null}
    </div>
  );
};

const CourseCell = ({ value }) => {
  const courses = Array.isArray(value)
    ? value
        .map(
          (course) =>
            course?.courseTitle || course?.title || course?.name || course,
        )
        .filter(Boolean)
    : typeof value === "string"
      ? value
          .split(",")
          .map((course) => course.trim())
          .filter(Boolean)
      : value
        ? [value]
        : [];
  const courseText = courses.join(", ");
  const hasMore = courses.length > 1;

  return (
    <span className="group relative flex items-center gap-2 text-sm text-[#111827]">
      <span className="truncate">{courses[0] || "-"}</span>
      {hasMore ? (
        <span className="shrink-0 text-xs text-[#6B7280]">
          +{courses.length - 1} more
        </span>
      ) : null}
      {hasMore ? (
        <span className="pointer-events-none absolute left-0 top-full z-10 mt-2 hidden w-max max-w-[320px] rounded-lg bg-[#111827] px-3 py-2 text-xs text-white shadow-lg group-hover:block">
          {courseText}
        </span>
      ) : null}
    </span>
  );
};

const formatPermissionLabel = (text) =>
  text
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const RoutePermissionsCell = ({ value }) => {
  const permissions = Array.isArray(value) ? value : [];

  if (!permissions.length) {
    return <span className="text-sm text-[#6B7280]">-</span>;
  }

  return (
    <div className="space-y-0.5 text-sm leading-snug">
      {permissions.map((item, index) => {
        const routeLabel = formatPermissionLabel(item?.route || "");
        const permissionLabels = (item?.permissions || [])
          .map((permission) => formatPermissionLabel(permission))
          .join(", ");

        return (
          <p key={`${item?.route}-${index}`} className="text-[#111827]">
            <span className="font-medium">{routeLabel || "-"}</span>
            {permissionLabels ? (
              <span className="text-[#6B7280]"> — {permissionLabels}</span>
            ) : null}
          </p>
        );
      })}
    </div>
  );
};

const getAccessRoleMenuItems = (
  row,
  { onChangeRole, onToggleStatus, onRemove },
) => {
  const status = (row.status || "").toLowerCase();
  const items = [{ label: "Change Role", handler: onChangeRole }];

  if (status === "inactive") {
    items.push({ label: "Active", handler: onToggleStatus });
  } else if (status === "active") {
    items.push({
      label: "Inactive",
      handler: onToggleStatus,
      className: "text-[#DC2626]",
    });
  } else if (status === "pending") {
    items.push({ label: "Remove", handler: onRemove });
  }

  return items;
};

const getStudentMenuItems = (
  row,
  { onViewProfile, onViewProgress, onToggleStatus },
) => {
  const status = (row.status || "").toLowerCase();
  const items = [{ label: "View Profile", handler: onViewProfile }];

  if (status === "inactive") {
    items.push({ label: "Active", handler: onToggleStatus });
  } else if (status === "active") {
    items.push({
      label: "Inactive",
      handler: onToggleStatus,
      className: "text-[#FF0000]",
    });
    items.push({ label: "View Progress", handler: onViewProgress });
  } else {
    items.push({
      label: "Active",
      handler: onToggleStatus,
      className: "text-[#22C55E]",
    });
  }

  return items;
};

const getInstructorMenuItems = (row, { onViewProfile, onToggleStatus }) => {
  const currentStatus = (row.status || "").toLowerCase();

  const allStatuses = [
    { value: "active", label: "Active", className: "text-[#22C55E]" },
    { value: "inactive", label: "Inactive", className: "text-[#DC2626]" },
    { value: "pending", label: "Pending", className: "text-[#F59E0B]" },
    { value: "flagged", label: "Flagged", className: "text-[#EF4444]" },
  ];

  return [
    { label: "View Profile", handler: onViewProfile },
    ...allStatuses
      .filter((status) => status.value !== currentStatus)
      .map((status) => ({
        label: status.label,
        className: status.className,
        handler: () =>
          onToggleStatus({
            ...row,
            nextStatus: status.value,
          }),
      })),
  ];
};

const RowActionsMenu = ({
  row,
  rowId,
  isOpen,
  onToggle,
  variant = "accessRole",
  onChangeRole,
  onEdit,
  onRemove,
  onToggleStatus,
  onViewProfile,
  onViewProgress,
}) => {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const updatePosition = () => {
      if (!buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();

      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right,
      });
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (
        buttonRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }

      onToggle(null);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const displayName =
    row.name || row.roleName || row.userEmail || row.role || "user";

  const menuItems =
    variant === "addedRole"
      ? [
          { label: "Edit", handler: onEdit },
          { label: "Remove", handler: onRemove },
        ]
      : variant === "student"
        ? getStudentMenuItems(row, {
            onViewProfile,
            onViewProgress,
            onToggleStatus,
          })
        : variant === "instructor"
          ? getInstructorMenuItems(row, {
              onViewProfile,
              onToggleStatus,
            })
          : getAccessRoleMenuItems(row, {
              onChangeRole,
              onToggleStatus,
              onRemove,
            });

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        aria-label={`Open actions for ${displayName}`}
        aria-expanded={isOpen}
        onClick={() => onToggle(isOpen ? null : rowId)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#111827] transition hover:bg-[#F8EFF8] hover:text-[#923D8C]"
      >
        <MoreHorizontal className="h-5 w-5" />
      </button>

      {isOpen && menuPosition
        ? createPortal(
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                top: menuPosition.top,
                left: menuPosition.left,
                transform: "translateX(-100%)",
              }}
              className="z-[9999] min-w-[148px] border border-[#938A96] bg-[#EAE9E9] py-1 shadow-lg"
            >
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className={[
                    "block w-full px-4 py-2 text-left text-sm transition hover:bg-[#DDDCDC]",
                    item.className || "text-black",
                  ].join(" ")}
                  onClick={() => {
                    item.handler?.(row);
                    onToggle(null);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

const Table = ({
  columns,
  rows,
  rowKey = "id",
  selectedRows,
  onToggleRow,
  onToggleAll,
  allVisibleSelected,
  statusStyleMap,
  isLoading = false,
  showActions = true,
  actionsMenuVariant = null,
  onChangeRole,
  onEdit,
  onRemove,
  onToggleStatus,
  onViewProfile,
  onViewProgress,
}) => {
  const [openActionRowId, setOpenActionRowId] = useState(null);
  const colSpan = columns.length + 1 + (showActions ? 1 : 0);
  const [showLoader, setShowLoader] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);
  return (
    <div className="overflow-x-auto">
      <table className=" w-full border-collapse">
        <thead className="">
          <tr className="bg-[#923D8C33] px-10 text-left text-sm font-medium text-black">
            <th className="whitespace-nowrap px-4 py-2.5 min-w-[120px]">
              <button
                type="button"
                className="flex flex-nowrap items-center gap-3 whitespace-nowrap"
                onClick={onToggleAll}
              >
                <CheckboxIcon checked={allVisibleSelected} />
                <span className="shrink-0 text-sm font-medium">Select All</span>
              </button>
            </th>

            {columns.map((column) => (
              <th
                key={column.key}
                className={column.headerClassName || "px-4 py-2.5"}
              >
                {column.label}
              </th>
            ))}

            {showActions ? (
              <th className="w-[100px] px-4 py-2.5">Actions</th>
            ) : null}
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={colSpan}
                className="px-4 py-10 text-center text-sm text-[#6B7280]"
              >
                {isLoading || showLoader ? (
                  <div className="flex items-center justify-center">
                    <Loader size="lg" className="text-[#923D8C]" />
                  </div>
                ) : (
                  <p>No data available</p>
                )}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const isSelected = selectedRows.includes(row[rowKey]);

              return (
                <tr key={row[rowKey]} className=" last:border-b-0">
                  <td className="px-5 py-3 align-middle">
                    <button
                      type="button"
                      onClick={() => onToggleRow(row[rowKey])}
                      aria-label={`Select ${row.name || row.role}`}
                      className="flex items-center"
                    >
                      <CheckboxIcon checked={isSelected} />
                    </button>
                  </td>

                  {columns.map((column) => (
                    <td
                      key={`${row[rowKey]}-${column.key}`}
                      className={
                        column.cellClassName || "px-4 py-3 align-middle"
                      }
                    >
                      {column.key === "name" ? (
                        <UserIdentity row={row} />
                      ) : column.key === "course" ? (
                        <CourseCell value={row[column.key]} />
                      ) : column.key === "routePermissions" ? (
                        <RoutePermissionsCell value={row[column.key]} />
                      ) : column.render ? (
                        column.render(row)
                      ) : column.key === "status" && row.status ? (
                        <StatusBadge
                          status={row.status}
                          note={row.note}
                          statusStyleMap={statusStyleMap}
                        />
                      ) : (
                        row[column.key]
                      )}
                    </td>
                  ))}

                  {showActions ? (
                    <td className="px-4 py-4 align-middle">
                      {actionsMenuVariant ? (
                        <RowActionsMenu
                          row={row}
                          rowId={row[rowKey]}
                          isOpen={openActionRowId === row[rowKey]}
                          onToggle={setOpenActionRowId}
                          variant={actionsMenuVariant}
                          onChangeRole={onChangeRole}
                          onEdit={onEdit}
                          onRemove={onRemove}
                          onToggleStatus={onToggleStatus}
                          onViewProfile={onViewProfile}
                          onViewProgress={onViewProgress}
                        />
                      ) : (
                        <button
                          type="button"
                          aria-label={`Open actions for ${row.name || row.roleName || row.role}`}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#111827] transition hover:bg-[#F8EFF8] hover:text-[#923D8C]"
                        >
                          <MoreHorizontal className="h-5 w-5" />
                        </button>
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
